import { Browser } from "@capacitor/browser";
import { getPaymentWindowStatusAction } from "@/app/actions/payment-window-status";
import { logNativeFlow } from "@/lib/native-flow-diagnostics";

/** 딥링크 처리기(payment-deep-link)가 이 결제를 맡았다는 표시 — 창 닫힘 처리와 겹치지 않게. */
export const PAYMENT_DEEPLINK_KEY = "locarora_payment_deeplink";

function deepLinkHandled(paymentId: string): boolean {
  try {
    return sessionStorage.getItem(PAYMENT_DEEPLINK_KEY) === paymentId;
  } catch {
    return false;
  }
}

/**
 * 앱 결제 창(@capacitor/browser)을 열기 직전에 부른다. 창이 딥링크 없이 닫히면(고객이 결제 직후
 * 완료/✕ 를 누르거나 iOS 가 앱을 다시 열지 못함) 서버에 결제 여부를 묻고, 결제됐으면 완료 화면으로
 * 보낸다. 결제 안 됐으면 결제 화면에 그대로 둔다(10-30, 중복 결제 방지).
 */
export async function watchPaymentWindowClose(args: {
  paymentId: string;
  gateway: "stripe" | "portone";
  locale: string;
  orderGroupId?: string | null;
  stripeSessionId?: string | null;
}): Promise<void> {
  const { paymentId } = args;
  try {
    sessionStorage.removeItem(PAYMENT_DEEPLINK_KEY);
  } catch {
    // 무시
  }
  const handle = await Browser.addListener("browserFinished", async () => {
    await handle.remove().catch(() => {});
    if (deepLinkHandled(paymentId)) return;
    logNativeFlow("payment-window", "closed-without-deeplink", { paymentId, gateway: args.gateway });
    const status = await getPaymentWindowStatusAction({
      paymentId,
      gateway: args.gateway,
      stripeSessionId: args.stripeSessionId ?? null,
    }).catch(() => "not_paid" as const);
    logNativeFlow("payment-window", "status", { paymentId, status });
    // 확인하는 동안 딥링크가 도착했으면 그쪽이 이미 이동했다.
    if (status !== "paid" || deepLinkHandled(paymentId)) return;
    try {
      sessionStorage.setItem("locarora_payment_completed", paymentId);
    } catch {
      // 무시
    }
    const params = new URLSearchParams({ paymentId });
    if (args.orderGroupId) params.set("orderGroupId", args.orderGroupId);
    window.location.href = `/${args.locale}/checkout/success?${params.toString()}`;
  });
}
